import type { RealIndex } from '../../interfaces/maestra';
import { altasForPattern } from '../../constants/realBadge';
import {
  dinheiroDoRelatorio, fmtBRL, FREQ_LABELS, PAGANTE_LABELS, PREMIOS_LABELS_V3, PROFILE_BITS,
} from '../../constants/realCopy';
import { FIXOS, INTRO_DA_DIMENSAO, LEITURA_DA_DIMENSAO, LEITURAS_CURTAS } from '../../constants/realTextos';
import {
  comentariosDaDimensao, retratoDoPerfil, seloDaDimensao, statusDaBarra,
} from '../../services/realEngine/comentarios';
import {
  AVISOS, avisosSemLugarProprio, conviteADetalhar, detalhouOE, ehLegado, linhasDaDimensao, resumoDoE, SIIC_MENSAL,
} from '../../services/realEngine/relatorio';
import { dimNarrative } from '../../constants/realNarrative';

// O RELATÓRIO DO DIAGNÓSTICO REAL, em dados: tudo o que a tela mostra, montado a partir do
// resultado que o motor gravou (o `realIndex`) e dos textos do núcleo. NADA AQUI CALCULA O MÉTODO:
// as notas, os selos, as linhas, os comentários, o retrato e o resumo do E são do núcleo
// (`services/realEngine`), os mesmos da tela nativa e do PDF. Aqui só se escolhe o que mostrar.
//
// Copiado de `pages/ArtistCreate/DiagnosticReport.tsx`, com os três ramos de versão:
// - v4, o relatório inteiro: selo, barra, intro, leitura, linhas, avisos e comentários;
// - v3, o mesmo cartão com as entradas cruas e a narrativa antiga (`dimNarrative`);
// - v1/v2, o cartão antigo, com as frases fixas por estado.

export type ChaveDaDimensao = 'r' | 'e' | 'a' | 'l';

export interface Chartmetric {
  monthly_listeners?: number | null;
  top_cities?: { name: string; country: string; listeners: number }[];
  audience?: { top_countries?: { name: string; code?: string | null; listeners?: number | null }[] } | null;
  playlists?: { count?: number; reach?: number; top?: { name: string; followers?: number; curator?: string | null; editorial?: boolean }[] } | null;
}

/** Números grandes em pt-BR ("2,5 mi", "12 mil"). */
export const numeroCurto = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')} mi`;
  if (n >= 1_000) return `${Math.round(n / 1000)} mil`;
  return String(n);
};

/** Sem travessão nos textos vindos do banco: a voz da marca reescreve a pausa como vírgula. */
const semTravessao = (s: string) => s.replace(/\s*[—–]\s*/g, ', ');

export const DIMENSOES: { chave: ChaveDaDimensao; letra: string; titulo: string; sub: string }[] = [
  { chave: 'r', letra: 'R', titulo: 'Reach', sub: 'Alcance' },
  { chave: 'e', letra: 'E', titulo: 'Earnings', sub: 'Ganhos' },
  { chave: 'a', letra: 'A', titulo: 'Audience', sub: 'Público real' },
  { chave: 'l', letra: 'L', titulo: 'Legitimacy', sub: 'Legitimação' },
];

// As frases por estado do cartão antigo (v1/v2): texto fixo do doc de conteúdo §3.
const FRASE_DO_LEGADO: Record<ChaveDaDimensao, { alto: string; baixo: string }> = {
  r: {
    alto: 'Sua música alcança gente além da sua bolha. O algoritmo e as playlists estão trabalhando por você.',
    baixo: 'Seu alcance digital ainda está abaixo do típico do mercado. Esse costuma ser o ponto de partida de quem quer crescer.',
  },
  e: {
    alto: 'Sua carreira já gera receita acima do típico. A música está pagando as contas, e mais.',
    baixo: 'Sua carreira ainda não se sustenta financeiramente. Isso é mais comum do que parece, e tem solução estratégica.',
  },
  a: {
    alto: 'Você tem público de verdade: gente que aparece, compra ingresso e segue a música. Isso é difícil de construir e vale muito.',
    baixo: 'Seu público comprometido ainda está em construção. A diferença entre quem te alcança e quem realmente te escolhe ainda é grande.',
  },
  l: {
    alto: 'Prêmios e imprensa já validam o seu trabalho. O mercado e a crítica reconhecem o que você faz.',
    baixo: 'Seu trabalho ainda não foi validado por prêmios ou imprensa de expressão. Esse reconhecimento costuma vir com estratégia, não só com talento.',
  },
};

// Os 16 perfis por "andar" (dimensões acesas), do Icon (4) ao Beginner (0).
const MAPA_DOS_PERFIS: { altas: number; nomes: string[] }[] = [
  { altas: 4, nomes: ['Icon'] },
  { altas: 3, nomes: ['Hit', 'Spotlight', 'Underpaid', 'Analog'] },
  { altas: 2, nomes: ['Digital', 'Potential', 'Hype', 'Rising', 'Outlier', 'Bet'] },
  { altas: 1, nomes: ['Influencer', 'Moneymaker', 'Paradox', 'Cult'] },
  { altas: 0, nomes: ['Beginner'] },
];

const chaveDoPerfil = (bits: Record<string, boolean>) => `${bits.r ? 1 : 0}${bits.e ? 1 : 0}${bits.a ? 1 : 0}${bits.l ? 1 : 0}`;

// As entradas cruas do legado v2, na forma que o cartão antigo lê.
const PREMIO_LEGADO = ['Nunca fui indicada nem premiada', 'Já fui indicada (sem ganhar)', 'Prêmio local/regional', 'Prêmio nacional', 'Prêmio internacional'];
const IMPRENSA_LEGADO = ['Nunca apareci na mídia', 'Repercussão local/regional', 'Repercussão nacional', 'Repercussão internacional'];
/* eslint-disable @typescript-eslint/no-explicit-any */
const entradasDaV2 = (i: any) => ({
  monthly_listeners: i?.spotifyListeners ?? null,
  sp_followers: i?.spotifyFollowers ?? null,
  social: { instagram: i?.igFollowers ?? null, tiktok: i?.tiktokFollowers ?? null, youtube: i?.youtubeMonthlyViews ?? null },
  faturamento: fmtBRL(Number(i?.showsPerMonth ?? 0) * Number(i?.cache ?? 0) + Number(i?.faturamentoForaShows ?? 0)),
  shows_pagos: String(i?.showsPerMonth ?? 0),
  maior_publico: String(i?.avgAudience ?? 0),
  premios: PREMIO_LEGADO[i?.premios] ?? 'Sem dado',
  imprensa: IMPRENSA_LEGADO[i?.imprensa] ?? 'Sem dado',
});

export interface LinhaDeDado {
  rotulo: string;
  valor: string;
  /** Autorrelato do questionário (a † do PDF): a Maestra não verifica. */
  declarado: boolean;
}

export interface FinancasDoE {
  /** A composição da receita anual, em reais: shows e cada fonte. */
  composicao: { rotulo: string; valor: number; texto: string }[];
  /** As fontes marcadas "não sei", com o aviso do núcleo. */
  naoInformado: { fontes: string; aviso: string } | null;
  cache: { rotulo: string; valor: number; texto: string }[];
  saude: { rotulo: string; valor: string; sinal?: 'positivo' | 'negativo' }[];
  avisoDoSaldo: string | null;
  notas: string[];
}

export interface DimensaoDoRelatorio {
  chave: ChaveDaDimensao;
  letra: string;
  titulo: string;
  sub: string;
  estado: 'apagada' | 'acesa' | 'topTier' | 'naoInformada';
  selo: string;
  /** 0..100, ou `null` no legado v1 (que não tem nota). */
  nota: number | null;
  /** "Faltam 12 pontos para acender" etc. (F3–F5), só da v3 em diante. */
  status: string | null;
  intro: { titulo: string; texto: string } | null;
  leitura: string | null;
  linhas: LinhaDeDado[];
  avisos: string[];
  /** A dica da Maestra: o que o artista pode fazer para a leitura ficar completa (o Spotify for
   *  Artists, no R). Não é alerta, é um conselho: a tela a mostra com a marca, e não com o "i". */
  dica: string | null;
  /** O E: o convite a detalhar, a estrutura (CNPJ, empresário) e as finanças. */
  convite: string | null;
  estrutura: { rotulo: string; tem: boolean }[] | null;
  financas: FinancasDoE | null;
  revela: { titulo: string; paragrafos: { lead: string; corpo: string }[] } | null;
}

const dinheiro = (n: number) => dinheiroDoRelatorio(Math.abs(n));

/** As finanças do E: o resumo do núcleo (v4) ou a conta mensal do legado v3. */
const financasDoE = (ri: any): FinancasDoE | null => {
  if (!detalhouOE(ri)) return null;
  const resumo = resumoDoE(ri);
  const rev = ri.revenue || {};
  const inputs = ri.inputs || {};
  const composicao: { rotulo: string; valor: number }[] = [];
  const ROTULO_DA_FONTE: Record<string, string> = { streaming: 'Streaming', direitos: 'Direitos', publi: 'Publicidade', aulas: 'Aulas', editais: 'Editais', venda: 'Venda / merch', outros: 'Outros' };
  if (resumo) {
    if (resumo.receitaShows > 0) composicao.push({ rotulo: 'Shows', valor: resumo.receitaShows });
    resumo.fontes.forEach((f) => { if (f.valor > 0) composicao.push({ rotulo: f.rotulo, valor: f.valor }); });
  } else {
    if (Number(rev?.shows) > 0) composicao.push({ rotulo: 'Shows', valor: Number(rev.shows) });
    Object.entries(rev?.sources || {}).forEach(([k, v]) => { if (Number(v) > 0) composicao.push({ rotulo: ROTULO_DA_FONTE[k] || k, valor: Number(v) }); });
  }
  const naoSei = resumo?.fontes.filter((f) => f.naoSei) ?? [];

  if (!resumo) {
    // Legado (v3): base mensal, sem saldo ajustado nem alíquota.
    const fat = Math.round(Number(rev.total ?? 0) * 12);
    const inv = Math.round(Number(inputs.investimento ?? 0));
    const saldo = fat - inv;
    return {
      composicao: composicao.map((c) => ({ ...c, texto: dinheiro(c.valor) })), naoInformado: null, cache: [],
      saude: [
        { rotulo: 'Faturamento', valor: dinheiro(fat) },
        { rotulo: 'Investimento', valor: dinheiro(inv) },
        { rotulo: 'Saldo', valor: `${saldo >= 0 ? '+' : '−'}${dinheiro(saldo)}`, sinal: saldo >= 0 ? 'positivo' : 'negativo' },
      ],
      avisoDoSaldo: null,
      notas: [],
    };
  }

  const saude: FinancasDoE['saude'] = [
    { rotulo: 'Receita', valor: dinheiro(resumo.receitaAnual) },
    { rotulo: 'Custos e investimento', valor: dinheiro(resumo.investimentoAnual) },
    { rotulo: 'Saldo', valor: `${resumo.saldo >= 0 ? '+' : '−'}${dinheiro(resumo.saldo)}`, sinal: resumo.saldo >= 0 ? 'positivo' : 'negativo' },
  ];
  // ⚠️ O SALDO AJUSTADO NÃO APARECE (v4.4): é pontuação do método, e não dinheiro da conta.
  if (resumo.receitaLiquidaEstimada != null) {
    saude.push({ rotulo: `Receita líquida estimada (${resumo.aliquotaRotulo})`, valor: dinheiro(resumo.receitaLiquidaEstimada) });
  }
  const notas = [
    // A frase fala do SALDO (v4.4, §7.10): é o mesmo texto do card do PDF.
    `A média mensal do setor cultural formal é ${fmtBRL(SIIC_MENSAL)} (SIIC/IBGE). Este saldo equivale a ${resumo.vezesOSetor.toFixed(1).replace('.', ',')}× esse patamar.`,
    ...(resumo.recomendarEmpresariamento
      ? ['Artistas com empresariamento faturam mais na Pesquisa Empresariamento 2025. É a estrutura que mais muda esta dimensão.']
      : []),
  ];
  return {
    // Um formato de dinheiro só no relatório inteiro (o curto do núcleo, "R$ 378 mil").
    composicao: composicao.map((c) => ({ ...c, texto: dinheiro(c.valor) })),
    naoInformado: naoSei.length ? { fontes: naoSei.map((f) => f.rotulo).join(', '), aviso: AVISOS.naoSei } : null,
    cache: resumo.cache.map((c) => ({ rotulo: c.rotulo, valor: c.valor, texto: dinheiro(c.valor) })),
    saude,
    avisoDoSaldo: resumo.saldo < 0 ? AVISOS.saldoNegativo : null,
    notas,
  };
};

/** Uma dimensão do boletim (v3 e v4): o cartão da spec do relatório. */
/**
 * O texto do núcleo diz "Faltam {x} pontos" para qualquer x, e com 1 sai "Faltam 1 pontos". A
 * concordância é acertada aqui, na leitura (o núcleo é o mesmo do app e não se mexe por isso).
 */
export const concordarComUm = (texto: string | null): string | null => (
  texto ? texto.replace(/\bFaltam 1 pontos\b/g, 'Falta 1 ponto') : texto
);

const dimensaoDoBoletim = (ri: any, dk: ChaveDaDimensao, cm: Chartmetric | null): DimensaoDoRelatorio => {
  const meta = DIMENSOES.find((d) => d.chave === dk)!;
  const high = !!ri.pattern[dk];
  const nota = Math.max(0, Math.min(100, Math.round(Number(ri.boletim?.[dk] ?? 0))));
  const inputs = ri.inputs || {};
  const rev = ri.revenue || {};
  const legado = ehLegado(ri);
  const num = (n: number | null | undefined) => (n == null ? 'Sem dado' : numeroCurto(Math.round(Number(n))));
  const linhas: LinhaDeDado[] = !legado
    ? linhasDaDimensao(ri, dk, cm).map((l) => ({
      rotulo: l.rotulo, valor: l.num != null ? numeroCurto(Math.round(l.num)) : (l.valor ?? 'Sem dado'), declarado: l.fonte === 'self',
    }))
    : dk === 'r' ? [
      { rotulo: 'Ouvintes Spotify', valor: num(cm?.monthly_listeners ?? inputs.spotifyListeners), declarado: false },
      { rotulo: 'Instagram', valor: num(inputs.igFollowers), declarado: false },
      { rotulo: 'TikTok', valor: num(inputs.tiktokFollowers), declarado: false },
      { rotulo: 'YouTube mensal', valor: num(inputs.youtubeMonthlyViews), declarado: false },
    ] : dk === 'e' ? [
      { rotulo: 'Receita mensal', valor: fmtBRL(Number(rev.total ?? 0)), declarado: true },
      { rotulo: 'Shows / mês', valor: String(inputs.showsPerMonth ?? 0), declarado: true },
      { rotulo: 'Cachê médio', valor: fmtBRL(Number(inputs.cache ?? 0)), declarado: true },
    ] : dk === 'a' ? [
      { rotulo: 'Shows / mês', valor: String(inputs.showsPerMonth ?? 0), declarado: true },
      { rotulo: '% público pagante', valor: inputs.fazBilheteria ? (PAGANTE_LABELS[inputs.pagantePct] ?? 'Sem dado') : 'Não faz bilheteria', declarado: true },
      { rotulo: 'Seguidores Spotify', valor: num(inputs.spotifyFollowers), declarado: false },
      { rotulo: 'Fãs Deezer', valor: num(inputs.deezerFans), declarado: false },
    ] : [
      { rotulo: 'Prêmios', valor: PREMIOS_LABELS_V3[Number(inputs.premios ?? 0)] ?? 'Sem dado', declarado: true },
      { rotulo: 'Imprensa', valor: inputs.imprensaRepercussao ? (FREQ_LABELS[inputs.imprensaFrequencia] ?? 'Sim') : 'Não', declarado: true },
      { rotulo: 'Playlists editoriais', valor: String(inputs.editorialPlaylists ?? cm?.playlists?.count ?? 0), declarado: false },
      // Sem dado ≠ "não toca": o airplay pode não ter vindo da Chartmetric.
      {
        rotulo: 'Execução em rádio',
        valor: inputs.radioAirplay == null ? 'Sem dado' : Number(inputs.radioAirplay) > 0 ? `${numeroCurto(Math.round(Number(inputs.radioAirplay)))} execuções` : 'Não',
        declarado: false,
      },
    ];

  const avisos: string[] = [];
  const dica = !legado && dk === 'r' && ri.flags?.autodeclarados?.length ? AVISOS.autodeclarado : null;
  if (!legado) {
    if (dk === 'a' && ri.flags?.aSemBilheteria) avisos.push(AVISOS.semBilheteria);
    if (dk === 'l' && ri.flags?.travaL) avisos.push(AVISOS.travaL);
  }

  let revela: DimensaoDoRelatorio['revela'] = null;
  if (legado) {
    const nar = dimNarrative(dk, ri);
    revela = { titulo: nar.headline, paragrafos: nar.paras.map((p: { lead: string; body: string }) => ({ lead: p.lead, corpo: p.body })) };
  } else {
    const comentarios = comentariosDaDimensao(ri, dk, { superficie: 'tela', chartmetric: cm });
    if (comentarios.length) revela = { titulo: '', paragrafos: comentarios.map((c) => ({ lead: c.lead, corpo: c.corpo ?? '' })) };
  }

  const resumo = dk === 'e' ? resumoDoE(ri) : null;
  const selo = seloDaDimensao(ri, dk);
  return {
    ...meta,
    estado: selo.estado,
    selo: selo.estado === 'topTier' ? 'Top Tier' : selo.estado === 'acesa' ? 'Acesa' : 'Apagada',
    nota,
    status: concordarComUm(statusDaBarra(ri, dk)),
    intro: legado ? null : { titulo: FIXOS.F21, texto: INTRO_DA_DIMENSAO[dk] },
    leitura: legado ? null : LEITURA_DA_DIMENSAO[dk][high ? 'alto' : 'baixo'],
    linhas,
    avisos,
    dica,
    convite: dk === 'e' ? conviteADetalhar(ri) : null,
    // Os dois sempre, positivo ou negativo: mostrar só a ausência virava repreensão (§3).
    estrutura: dk === 'e' ? [
      { rotulo: 'CNPJ', tem: resumo ? ri.raw?.temCnpj === true : !!inputs.temCnpj },
      { rotulo: 'Empresário', tem: resumo ? ri.raw?.temEmpresario === true : !!inputs.temEmpresario },
    ] : null,
    financas: dk === 'e' ? financasDoE(ri) : null,
    revela,
  };
};

/** Uma dimensão do cartão antigo (v1/v2). */
const dimensaoDoLegado = (ri: any, dk: ChaveDaDimensao, cm: Chartmetric | null): DimensaoDoRelatorio => {
  const meta = DIMENSOES.find((d) => d.chave === dk)!;
  const isV2 = ri.version === 2;
  const inputs = isV2 ? entradasDaV2(ri.inputs) : (ri.inputs || {});
  const semGanhos = !isV2 && !!ri.earningsUnknown;
  const high = !!ri.pattern[dk];
  const neutro = dk === 'e' && semGanhos;
  const num = (n: number | null | undefined) => (n == null ? 'Sem dado' : numeroCurto(Math.round(Number(n))));
  const linhas: Record<ChaveDaDimensao, LinhaDeDado[]> = {
    r: [
      { rotulo: 'Ouvintes mensais no Spotify', valor: num(cm?.monthly_listeners ?? inputs.monthly_listeners), declarado: false },
      ...(inputs.social?.instagram != null ? [{ rotulo: 'Instagram', valor: num(inputs.social.instagram), declarado: false }] : []),
      ...(inputs.social?.tiktok != null ? [{ rotulo: 'TikTok', valor: num(inputs.social.tiktok), declarado: false }] : []),
      ...(inputs.social?.youtube != null ? [{ rotulo: 'YouTube', valor: num(inputs.social.youtube), declarado: false }] : []),
    ],
    e: [{ rotulo: 'Faturamento mensal médio', valor: semGanhos ? 'Não informado' : String(inputs.faturamento ?? 'Sem dado'), declarado: false }],
    a: [
      { rotulo: isV2 ? 'Shows por mês' : 'Shows pagos (12 meses)', valor: String(inputs.shows_pagos ?? 'Sem dado'), declarado: false },
      { rotulo: isV2 ? 'Público médio por show' : 'Maior público ao vivo', valor: String(inputs.maior_publico ?? 'Sem dado'), declarado: false },
      { rotulo: 'Seguidores no Spotify', valor: num(inputs.sp_followers), declarado: false },
    ],
    l: [
      { rotulo: 'Prêmios', valor: String(inputs.premios ?? 'Sem dado'), declarado: false },
      { rotulo: 'Imprensa / mídia', valor: String(inputs.imprensa ?? 'Sem dado'), declarado: false },
    ],
  };
  return {
    ...meta,
    estado: neutro ? 'naoInformada' : high ? 'acesa' : 'apagada',
    selo: neutro ? 'Não informado' : high ? 'Acesa' : 'Apagada',
    nota: isV2 && ri.boletim ? Number(ri.boletim[dk]) : null,
    status: null,
    intro: null,
    leitura: neutro
      ? 'Você não informou o faturamento. Sem esse dado, consideramos E como baixo para o cálculo.'
      : FRASE_DO_LEGADO[dk][high ? 'alto' : 'baixo'],
    linhas: linhas[dk],
    avisos: [],
    dica: null,
    convite: null,
    estrutura: null,
    financas: null,
    revela: null,
  };
};

export const relatorioReal = (realIndex: RealIndex, chartmetric?: Chartmetric | null) => {
  const ri = realIndex as any;
  const cm = chartmetric ?? null;
  const doBoletim = ri.version === 3 || ri.version === 4;
  const { profile, pattern } = realIndex;
  const altas = altasForPattern(pattern);
  const cidades = cm?.top_cities ?? [];
  const paises = cm?.audience?.top_countries ?? [];
  const playlists = cm?.playlists?.top ?? [];

  return {
    perfil: {
      nome: profile.name,
      // O RETRATO do perfil (§5.2); o legado cai na descrição antiga.
      retrato: retratoDoPerfil(ri)?.texto ?? semTravessao(profile.description || ''),
      altas,
      dimensoes: DIMENSOES.map((d) => ({ ...d, acesa: !!pattern[d.chave] })),
    },
    /** O aviso de versão (§13.2): fala do documento inteiro, e não cabe em cartão nenhum. */
    avisoDeVersao: avisosSemLugarProprio(ri)[0]?.texto ?? null,
    dimensoes: DIMENSOES.map((d) => (doBoletim ? dimensaoDoBoletim(ri, d.chave, cm) : dimensaoDoLegado(ri, d.chave, cm))),
    /** O legado mantém os bullets "o que o seu diagnóstico revela" (sem retrato nem comentários). */
    insights: ehLegado(ri) && profile.insights?.length ? profile.insights.map(semTravessao) : [],
    audiencia: cidades.length || paises.length || playlists.length ? {
      cidades: cidades.slice(0, 5).map((c) => ({ nome: c.name, ouvintes: c.listeners })),
      paises: paises.slice(0, 6).map((c) => ({ nome: c.name, ouvintes: c.listeners ?? 0 })),
      playlists: playlists.slice(0, 10).map((p, i) => ({
        posicao: i + 1, nome: p.name, editorial: !!p.editorial, seguidores: p.followers != null ? numeroCurto(p.followers) : null,
      })),
      totalDePlaylists: cm?.playlists?.count ?? null,
    } : null,
    perfis: MAPA_DOS_PERFIS.map((andar) => ({
      altas: andar.altas,
      perfis: andar.nomes.map((nome) => {
        const bits = PROFILE_BITS[nome] as Record<string, boolean> | undefined;
        return {
          nome,
          atual: nome === profile.name,
          dimensoes: bits ? DIMENSOES.map((d) => ({ letra: d.letra, acesa: !!bits[d.chave] })) : null,
          leitura: bits ? (LEITURAS_CURTAS[chaveDoPerfil(bits)] ?? null) : null,
        };
      }),
    })),
  };
};

export type RelatorioReal = ReturnType<typeof relatorioReal>;
